import {
  CustomPageContainer,
  ProcessesTab,
  TabItemProps,
  TablePaginationContextProvider,
  TasksTab,
  UserIcon,
} from '@/components';
import { LabelIcon } from '@/components/LabelIcon/LabelIcon';
import { PageDetailsActions } from '@/components/PageDetailsActions/PageDetailsActions';
import { TabContainer } from '@/components/TabContainer';
import { TabsWrapper } from '@/components/TabsWrapper';
import { HttpError } from '@/core/http';
import { RoutePath } from '@/core/router/route-paths';
import { useToggleUserAccess } from '@/hooks/use-toggle-user-access';
import { useToggleUserStatus } from '@/hooks/use-toggle-user-status';
import { getProcessesColumnsNamedMapped } from '@/modules/Processes';
import { UserProcess } from '@/modules/Processes/domain';
import { useGetProcessesByUser } from '@/modules/Processes/queries';
import { UserTask } from '@/modules/Tasks/domain';
import { getTasksColumnsNamedMapped } from '@/modules/Tasks/pages/TaskDetailPage/utils';
import { useGetTasksByUser } from '@/modules/Tasks/queries/get-tasks-by-user';
import { HttpStatusCode } from 'axios';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { UserRateModel } from '../../domain/user-rate-model';
import { useGetUser, useGetUserRates } from '../../queries';
import { UserHeaderRow } from './components/HeaderRow';
import { HourlyRatesTab } from './components/HourlyRatesTab';
import { UserAdminTab } from './components/UserAdminTab/UserAdminTab';
import { UserEmployeeTab } from './components/UserEmployeeTab/UserEmployeeTab';

export const UserDetailPage = (): ReactElement => {
  const { userId: userIdParam } = useParams<{ userId: string }>();
  const userId = +userIdParam!;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { onChange: onStatusChange } = useToggleUserStatus();
  const { onChange: onAccessChange } = useToggleUserAccess();
  const { data: user, isError, error } = useGetUser(userId);

  useEffect(() => {
    if (isError && error) {
      if ((error as HttpError<unknown>)?.code === HttpStatusCode.NotFound) {
        navigate(RoutePath.notFound());
      } else {
        toast.error(t('error_get_user_details'));
      }
    }
  }, [isError, error, navigate, t]);

  const [status, setStatus] = useState<boolean>(false);

  const handleStatus = useCallback(
    (checked: boolean) => {
      if (!user?.id) {
        return toast.error(t('generic_errors'));
      }
      setStatus(checked);
      onStatusChange(checked, user.id);
    },
    [user, onStatusChange, t],
  );

  const handleAccess = useCallback(
    (checked: boolean) => {
      if (!user?.id) {
        return toast.error(t('generic_errors'));
      }
      setStatus(checked);
      onAccessChange(checked, user.id);
    },
    [user, onAccessChange, t],
  );

  const tabs: Array<TabItemProps> = useMemo(
    () => [
      {
        label: t('administration_privileges'),
        badgeNumber: user?.adminPrivilegesCount,
      },
      {
        label: t('employee'),
        badgeNumber: user?.businessAreasCount,
      },
      { label: t('processes'), badgeNumber: user?.processesCount },
      { label: t('tasks'), badgeNumber: user?.tasksCount },
      { label: t('hourly_rates') },
    ],
    [t, user],
  );

  const routeChange = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const tabList = useMemo(() => {
    if (!userId) {
      return [];
    }
    return [
      {
        tab: (
          <TablePaginationContextProvider>
            <UserAdminTab userId={userId} />
          </TablePaginationContextProvider>
        ),
      },
      {
        tab: (
          <TablePaginationContextProvider>
            <UserEmployeeTab userId={userId} />
          </TablePaginationContextProvider>
        ),
      },
      {
        tab: (
          <TabContainer<UserProcess>
            parentId={userId}
            useGetData={useGetProcessesByUser}
            getColumnsNamedMapped={getProcessesColumnsNamedMapped}
          >
            <ProcessesTab canAttachUser canCreate={false} userId={userId} />
          </TabContainer>
        ),
      },
      {
        tab: (
          <TabContainer<UserTask>
            parentId={userId}
            useGetData={useGetTasksByUser}
            getColumnsNamedMapped={getTasksColumnsNamedMapped}
          >
            <TasksTab canCreate />
          </TabContainer>
        ),
      },
      {
        tab: (
          <TabContainer<UserRateModel>
            parentId={userId}
            useGetData={useGetUserRates}
          >
            <HourlyRatesTab canCreate={user?.status} userId={userId} />
          </TabContainer>
        ),
      },
    ];
  }, [user?.status, userId]);

  const breadcrumbs = useMemo(() => {
    if (!user) return [];
    return [
      { label: t('users'), href: RoutePath.users() },
      {
        label: `${user.firstName} ${user.lastName}`,
      },
    ];
  }, [user, t]);

  return (
    <CustomPageContainer>
      <PageDetailsActions onBack={routeChange} breadcrumbs={breadcrumbs}>
        <LabelIcon
          label={t('user')}
          icon={<UserIcon className="size-5 text-blueNuit" />}
        />
      </PageDetailsActions>
      <UserHeaderRow
        user={user}
        handleStatus={handleStatus}
        handleAccess={handleAccess}
        status={status}
      />
      <TablePaginationContextProvider>
        <TabsWrapper tabs={tabs} tabList={tabList} />
      </TablePaginationContextProvider>
    </CustomPageContainer>
  );
};
